/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {EventEmitter} from '@angular/core';

import {BehaviorSubject} from 'rxjs';

import {isPresent} from '../utils/lang';

/**
 * A pagination page.
 *
 * Property marked as \/* readonly *\/ are anticipating for Typescript 2.0
 */
export interface Page {
  /**
   * This page number in the pagination system
   */
  /* readonly */
  number: number;

  /**
   * Index of the first item for this page
   */
  /* readonly */
  firstItem: number;

  /**
   * Index of the last item for this page
   */
  /* readonly */
  lastItem: number;
}

/**
 * Pagination interface with access to
 */
export interface Pagination {

  /* readonly */
  perPage: number;
  /* readonly */
  currentPage: Page;
  /* readonly */
  totalItems: number;
  /* readonly */
  lastPage: number;

  /* readonly */
  pageChanged: EventEmitter<Page>;
  pageChanged$: BehaviorSubject<Page>;

  hasNext(): boolean;

  hasPrevious(): boolean;

  next();

  previous();

  first();

  last();

  /**
   * Navigate to a specific (0-based) page number.
   */
  goToPage(page: number);

  /**
   * Returns the list of page numbers (1-based) to display in the pagination bar.
   * A value of {@link ELLIPSIS} (-1) represents a truncation gap ("...").
   * Example for 10 pages while on page 2: [1, 2, 3, 4, -1, 10]
   */
  pages(): number[];

  /**
   * Whether the pagination bar should be shown, i.e. there is more than one page.
   */
  hasMultiplePages(): boolean;

  resetPageIfNeeded(query: { search: string, sort: string });

  update(totalItems?: number);
}

/**
 * Sentinel value returned by {@link Pagination.pages} to represent a
 * truncation gap ("...") between page numbers.
 */
export const ELLIPSIS = -1;

/**
 * Number of page numbers displayed on each side of the current page.
 */
const PAGE_SIBLINGS = 2;

/**
 * Default implementation of the pagination system
 */
export class DefaultPagination implements Pagination {

  public perPage: number = 25;
  public lastPage: number;

  public totalItems: number = 0;

  public currentPage: Page = {
    firstItem: 0,
    lastItem: 0,
    number: 0
  };

  public pageChanged: EventEmitter<Page> = new EventEmitter<Page>();
  public pageChanged$: BehaviorSubject<Page> = new BehaviorSubject<Page>(this.currentPage);

  private prevQuery: { search: string, sort: string } = {search: '', sort: ''};

  constructor(perPage?: number) {
    if (isPresent(perPage)) {
      this.perPage = perPage;
    }
  }

  public update(totalItems?: number) {
    if (totalItems !== undefined) {
      this.totalItems = totalItems;
    }
    this.lastPage = this.totalItems % this.perPage === 0 ?
      (this.totalItems / this.perPage) - 1 :
      Math.floor(this.totalItems / this.perPage);

    this.currentPage.firstItem = this.totalItems === 0 ? 0 : (this.currentPage.number * this.perPage) + 1;
    this.currentPage.lastItem = this.totalItems === 0 ? 0 : this.currentPage.number === this.lastPage ?
      this.totalItems :
      (this.currentPage.number + 1) * this.perPage;
  }

  public hasNext(): boolean {
    return this.currentPage.number !== this.lastPage;
  }

  public hasPrevious(): boolean {
    return this.currentPage.number > 0;
  }

  public first() {
    this.goToPage(0);
  }

  public last() {
    this.goToPage(this.lastPage);
  }

  public next() {
    if (this.currentPage.number < this.lastPage) {
      this.goToPage(this.currentPage.number + 1);
    }
  }

  public previous() {
    if (this.currentPage.number > 0) {
      this.goToPage(this.currentPage.number - 1);

    }
  }

  public resetPageIfNeeded(query: { search: string, sort: string } = {search: '', sort: ''}) {
    if (this.prevQuery.search !== query.search || this.prevQuery.sort !== query.sort) {
      this.currentPage.number = 0;
    }
    this.prevQuery = query;
  }

  /**
   * Builds the list of page numbers (1-based) to display, inserting {@link ELLIPSIS}
   * sentinels where pages are truncated. The first and last pages are always shown.
   */
  public pages(): number[] {
    const totalPages = this.totalItems === 0 || this.lastPage === undefined ? 0 : this.lastPage + 1;
    if (totalPages <= 1) {
      return totalPages === 1 ? [1] : [];
    }

    const current = this.currentPage.number + 1;
    const left = Math.max(2, current - PAGE_SIBLINGS);
    const right = Math.min(totalPages - 1, current + PAGE_SIBLINGS);

    const result: number[] = [1];
    if (left > 2) {
      result.push(ELLIPSIS);
    }
    for (let page = left; page <= right; page++) {
      result.push(page);
    }
    if (right < totalPages - 1) {
      result.push(ELLIPSIS);
    }
    result.push(totalPages);
    return result;
  }

  /**
   * There is more than one page to navigate (at least 2). Used to hide the
   * pagination bar entirely when there is no data or a single page.
   */
  public hasMultiplePages(): boolean {
    return this.lastPage !== undefined && this.lastPage > 0;
  }

  public goToPage(page: number) {
    if (page < 0 || page > this.lastPage || page === this.currentPage.number) {
      return;
    }
    this.currentPage.number = page;
    this.update();
    this.pageChanged.next(this.currentPage);
    this.pageChanged$.next(this.currentPage);
  }
}
